import { z } from "zod";
import { db } from "../../core/data/local/db";

/**
 * The terminal's copy of every recipe (data-model.md "Local state on the
 * terminal").
 *
 * ONE DOCUMENT IN `meta`, NOT A DEXIE STORE. Core already provides a generic
 * key/value store, and using it keeps every inventory noun out of
 * `src/core/data/local/db.ts` (Principle III) — no schema version, no store
 * named for a module.
 *
 * PULLED, NEVER PUSHED, AND NEVER ON THE SALE PATH. Refreshed from the
 * authenticated `GET /api/inventory/replica` when the till loads; a sale reads
 * whatever copy is here, offline or not, so the recipe in force is the one this
 * terminal held when the sale was rung (FR-117).
 */

export interface ReplicaLine {
  ingredientId: string;
  /** Thousandths of the ingredient's unit, per one unit sold. */
  quantity: number;
}

export interface ReplicaRecipe {
  id: string;
  menuItemId: string;
  variantId: string | null;
  modifierId: string | null;
  lines: ReplicaLine[];
}

export interface InventoryReplica {
  recipes: ReplicaRecipe[];
  generatedAt: string;
}

export const REPLICA_META_KEY = "inventory.replica";

const replicaSchema = z.object({
  recipes: z.array(
    z.object({
      id: z.string(),
      menuItemId: z.string(),
      variantId: z.string().nullable(),
      modifierId: z.string().nullable(),
      lines: z.array(z.object({ ingredientId: z.string(), quantity: z.number().int() })),
    }),
  ),
  generatedAt: z.string(),
});

const EMPTY: InventoryReplica = { recipes: [], generatedAt: "" };

let cached: InventoryReplica | null = null;

/**
 * The replica, from memory after the first read.
 *
 * A missing or unreadable copy is an EMPTY replica, not an error: every line
 * then resolves to nothing, the sale's envelope lists them as unmapped, and the
 * sale itself is untouched (FR-118).
 */
export async function loadReplica(): Promise<InventoryReplica> {
  if (cached) return cached;
  const row = await db.meta.get(REPLICA_META_KEY);
  const parsed = replicaSchema.safeParse(row?.value);
  cached = parsed.success ? parsed.data : EMPTY;
  return cached;
}

/** Replace the replica wholesale. Throws on a malformed payload rather than storing it. */
export async function hydrateReplica(payload: unknown): Promise<InventoryReplica> {
  const replica = replicaSchema.parse(payload);
  await db.meta.put({ key: REPLICA_META_KEY, value: replica });
  cached = replica;
  return replica;
}

/** Drop the in-memory copy. The stored one stays — disabling never destroys (FR-085). */
export function forgetReplicaCache(): void {
  cached = null;
}

/**
 * Pull a fresh replica. A no-op, not an error, when offline or refused — the
 * copy already here keeps working, exactly as the WiFi voucher pool does.
 */
export async function refreshInventoryReplica(sessionToken: string): Promise<boolean> {
  let response: Response;
  try {
    response = await fetch("/api/inventory/replica", {
      headers: { authorization: `Bearer ${sessionToken}` },
    });
  } catch {
    return false;
  }
  if (!response.ok) return false;
  await hydrateReplica(await response.json());
  return true;
}
